import React, { useMemo, useState } from "react";
import {
  FIELD_TASKS,
  GATE_CHECKS,
  KEY_TASKS_FLAT,
  KILL_LIST_ITEMS,
  SPRINT_TASKS,
  SOURCE_LABELS,
} from "../../../constants/activationConsoleData";
import { copyActivationText } from "./activationClipboard";

function KpiCard({ cls, label, val, target, fillPct, onOpen }) {
  return (
    <button type="button" className={`ac-kpi-card ${cls}`} onClick={onOpen}>
      <div className="ac-kpi-lbl">{label}</div>
      <div className="ac-kpi-val ac-serif">{val}</div>
      <div className="ac-kpi-target">{target}</div>
      <div className="ac-kpi-prog">
        <div className="ac-kpi-prog-fill" style={{ width: `${fillPct}%` }} />
      </div>
    </button>
  );
}

function ProgRow({ label, fill, n, color }) {
  return (
    <div className="ac-prog-row">
      <div className="ac-prog-lbl">{label}</div>
      <div className="ac-prog-track">
        <div className="ac-prog-fill" style={{ width: `${fill}%`, background: color }} />
      </div>
      <div className="ac-prog-n">{n}</div>
    </div>
  );
}

function WeekColumn({ week, tasks, state, toggleTask, gllOpen, setGllOpen }) {
  const wk = week === "week1" ? "Week 1" : "Week 2";
  const theme = week === "week1" ? "Seed + Anchor" : "Reinforce + Loop";
  const badge = week === "week1" ? "active" : "upcoming";
  const focus =
    week === "week1"
      ? 'Send outreach. Build the "This Week" artifact. Get the first mention in real life.'
      : 'Follow up, seed chat interception, watch for "what\'s that?" moments. The flywheel starts here.';

  return (
    <div className="ac-week-card">
      <div className="ac-wc-head">
        <div>
          <div className="ac-wc-week">{wk}</div>
          <div className="ac-wc-theme">{theme}</div>
        </div>
        <span className={`ac-wc-badge ${badge === "active" ? "badge-active" : "badge-upcoming"}`}>
          {badge === "active" ? "Active" : "Upcoming"}
        </span>
      </div>
      <div className="ac-wc-body">
        <div className="ac-wc-focus">{focus}</div>
        {tasks.map((t) => {
          const done = !!state.tasks[t.id];
          const hasGll = t.good && t.bad;
          return (
            <div key={t.id} className={`ac-day-row ${done ? "done-row" : ""}`}>
              <div className="ac-dr-day">{t.day}</div>
              <div className="ac-dr-content">
                <div className="ac-dr-top">
                  <button
                    type="button"
                    className={`ac-dr-check ${done ? "checked" : ""}`}
                    onClick={() => toggleTask(t.id)}
                    aria-pressed={done}
                  />
                  <div className={`ac-dr-task ${done ? "done-text" : ""}`}>
                    {t.task}
                    {t.key ? <span style={{ color: "var(--amber)", fontWeight: 700 }}> ★</span> : null}
                  </div>
                  <span className={`ac-dr-tag tag-${t.tag}`}>{t.tag}</span>
                  {hasGll ? (
                    <button
                      type="button"
                      className="ac-dr-gll-toggle"
                      onClick={() => setGllOpen((o) => ({ ...o, [t.id]: !o[t.id] }))}
                    >
                      ?
                    </button>
                  ) : null}
                </div>
                {hasGll ? (
                  <div className={`ac-dr-gll ${gllOpen[t.id] ? "open" : ""}`}>
                    <div className="ac-dr-gll-col ac-dr-gll-yes">
                      <div className="ac-dr-gll-head">✓ Good looks like</div>
                      {t.good}
                    </div>
                    <div className="ac-dr-gll-col ac-dr-gll-no">
                      <div className="ac-dr-gll-head">✗ Not this</div>
                      {t.bad}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ActivationSprintTab({
  state,
  derived,
  getTaskScript,
  toggleTask,
  dtnMarkDone,
  dtnSkip,
  toggleFieldCheck,
  toggleGateCheck,
  qrTestPass,
  addSignal,
  addFriction,
  onOpenKpi,
}) {
  const [gateBodyOpen, setGateBodyOpen] = useState(true);
  const [qrResult, setQrResult] = useState(null);
  const [dtnGoodOpen, setDtnGoodOpen] = useState(false);
  const [gllOpen, setGllOpen] = useState({});
  const [weekVerdict, setWeekVerdict] = useState(null);
  const [signalText, setSignalText] = useState("");
  const [signalType, setSignalType] = useState("strong");
  const [signalSource, setSignalSource] = useState("organizer");
  const [frictionText, setFrictionText] = useState("");

  const { kpis } = state;
  const script = derived.dtnTask ? getTaskScript(derived.dtnTask.id) : null;

  const handshakeProgress = useMemo(
    () => state.clubs.filter((c) => c.status !== "pending").length,
    [state.clubs]
  );
  const activeClubKpi = useMemo(
    () => state.clubs.filter((c) => c.status === "anchor" || c.status === "active").length,
    [state.clubs]
  );

  const prog = useMemo(() => {
    const t = kpis.travelers > 0;
    return {
      p1: { w: Math.min(100, (handshakeProgress / 10) * 100), label: `${handshakeProgress}/10` },
      p2: { w: Math.min(100, (kpis.mentions / 6) * 100), label: `${kpis.mentions}/6` },
      p3: { w: t ? 100 : 0, label: t ? "30/30" : "0/30" },
      p4: { w: Math.min(100, (kpis.sharing / 5) * 100), label: `${kpis.sharing}/5` },
      p5: { w: t ? 100 : 0, label: `${t ? 1 : 0}/1` },
    };
  }, [kpis, handshakeProgress]);

  const kpiFill = (key, max) => Math.min(100, (state.kpis[key] / max) * 100);

  const handleQrPass = () => {
    setQrResult("pass");
    qrTestPass();
  };
  const handleQrFail = () => {
    setQrResult("fail");
  };

  const handleAddSignalInline = () => {
    const t = signalText.trim();
    if (!t) return;
    addSignal(t, signalType, signalSource);
    setSignalText("");
  };

  return (
    <>
      <div
        className={`ac-no-signal-warning ${derived.showNoSignalWarning ? "show" : ""}`}
      >
        ⚠ Rule: No signal logged in 3+ days → go to a run today.
      </div>

      <div
        className={`ac-readiness-gate ac-topbar-full ${derived.gateCleared ? "gate-clear" : "gate-locked"}`}
      >
        <button
          type="button"
          className="ac-rg-header"
          onClick={() => setGateBodyOpen((o) => !o)}
          aria-expanded={gateBodyOpen}
        >
          <div className="ac-rg-left">
            <div className="ac-rg-icon">{derived.gateCleared ? "✅" : "🔒"}</div>
            <div>
              <div className="ac-rg-title">Product Readiness Gate</div>
              <div className="ac-rg-subtitle">
                {derived.gateCleared
                  ? "Product is ready — proceed with field strategy"
                  : `${GATE_CHECKS.length - derived.gateDone} item${
                      GATE_CHECKS.length - derived.gateDone !== 1 ? "s" : ""
                    } remaining before placing QR cards or pushing to clubs`}
              </div>
            </div>
          </div>
          <span className="ac-rg-status-pill">
            {derived.gateCleared ? "CLEARED ✓" : `${derived.gateDone}/${GATE_CHECKS.length} CHECKED`}
          </span>
        </button>
        <div className={`ac-rg-body ${gateBodyOpen ? "open" : ""}`} id="rg-body">
          <div className="ac-rg-rule">
            ⛔ If ANY item below is unchecked → do not place QR cards → do not push to clubs → fix the
            product first
          </div>
          <div className="ac-rg-checks">
            {GATE_CHECKS.map((g) => (
              <button
                key={g.id}
                type="button"
                className={`ac-rg-check-item ${state.gateChecks[g.id] ? "checked" : ""}`}
                onClick={() => toggleGateCheck(g.id)}
              >
                <div className="ac-rg-check-box" />
                <div>
                  <div className="ac-rg-check-label">{g.label}</div>
                  <div className="ac-rg-check-note">{g.note}</div>
                  <div className="ac-rg-check-code">{g.code}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="ac-rg-qr-test">
            <div className="ac-rg-qr-title">QR Entry Test — run before every field session</div>
            <div className="ac-rg-qr-step">
              1. Open <strong style={{ color: "white" }}>runcards.app/nyc</strong> in incognito on your
              phone (no account, cold load)
              <br />
              2. Start a timer. NYC run names + times visible within{" "}
              <strong style={{ color: "white" }}>3 seconds</strong> without touching anything?
              <br />
              3. Send runcards.app/nyc in iMessage. Does the preview show a title + image?
            </div>
            <div className="ac-rg-qr-buttons">
              <button type="button" className="ac-rg-qr-btn ac-rg-qr-pass" onClick={handleQrPass}>
                ✓ All pass — place cards
              </button>
              <button type="button" className="ac-rg-qr-btn ac-rg-qr-fail" onClick={handleQrFail}>
                ✗ Something failed — fix first
              </button>
            </div>
            {qrResult === "pass" && (
              <div className="ac-rg-qr-result pass">
                ✓ Product cleared. Place QR cards and run the field strategy.
              </div>
            )}
            {qrResult === "fail" && (
              <div className="ac-rg-qr-result fail">
                ✗ Product not ready. Check which gate item failed and fix it first. Do not place cards.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="ac-dtn ac-topbar-full" id="do-this-now">
        {derived.dtnTask ? (
          <>
            <div>
              <div className="ac-dtn-header">
                <div>
                  <div className="ac-dtn-eyebrow">
                    Do This Now — {derived.dtnDoneCount}/{KEY_TASKS_FLAT.length} critical tasks done
                  </div>
                  <div className="ac-dtn-action ac-serif">
                    {derived.dtnTask.task.replace("★", "")}
                  </div>
                </div>
                <button
                  type="button"
                  className="ac-dtn-good-toggle"
                  onClick={() => setDtnGoodOpen((o) => !o)}
                >
                  What good looks like
                </button>
              </div>
              {script ? (
                <div className="ac-dtn-footer">
                  <div className="ac-dtn-copy-msg">{script}</div>
                  <div className="ac-dtn-buttons">
                    <button
                      type="button"
                      className="ac-dtn-btn-copy"
                      onClick={() =>
                        copyActivationText(script, () => {
                          /* optional toast */
                        })
                      }
                    >
                      Copy message
                    </button>
                    <button
                      type="button"
                      className="ac-dtn-btn-done"
                      onClick={() => dtnMarkDone(derived.dtnTask.id)}
                    >
                      Mark done ✓
                    </button>
                    <button
                      type="button"
                      className="ac-dtn-btn-skip"
                      onClick={() => dtnSkip(derived.dtnTask.id)}
                    >
                      Skip for now
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ac-dtn-footer" style={{ justifyContent: "flex-end", paddingTop: 0 }}>
                  <div className="ac-dtn-buttons" style={{ flexDirection: "row" }}>
                    <button
                      type="button"
                      className="ac-dtn-btn-done"
                      onClick={() => dtnMarkDone(derived.dtnTask.id)}
                    >
                      Mark done ✓
                    </button>
                    <button
                      type="button"
                      className="ac-dtn-btn-skip"
                      onClick={() => dtnSkip(derived.dtnTask.id)}
                    >
                      Skip for now
                    </button>
                  </div>
                </div>
              )}
              <div className={`ac-dtn-good-looks ${dtnGoodOpen ? "open" : ""}`}>
                <div className="ac-dtn-gl-col ac-dtn-gl-yes">
                  <div className="ac-dtn-gl-lbl">Good looks like</div>
                  {derived.dtnTask.good || "Specific, named result."}
                </div>
                <div className="ac-dtn-gl-col ac-dtn-gl-no">
                  <div className="ac-dtn-gl-lbl">Not this</div>
                  {derived.dtnTask.bad || "Vague completion."}
                </div>
              </div>
            </div>
            <div className="ac-dtn-progress">
              <div
                className="ac-dtn-progress-fill"
                style={{ width: `${derived.dtnProgressPct}%` }}
              />
            </div>
          </>
        ) : (
          <>
            <div className="ac-dtn-all-done">
              <div className="ac-dtn-all-done-txt ac-serif">All critical tasks complete.</div>
              <div className="ac-dtn-all-done-sub">Check the Signal Log — is this week&apos;s verdict in?</div>
            </div>
            <div className="ac-dtn-progress">
              <div className="ac-dtn-progress-fill" style={{ width: "100%" }} />
            </div>
          </>
        )}
      </div>

      {["before", "during", "after"].map((phase) => (
        <div key={phase} className="ac-field-panel" id={`field-${phase}`}>
          <div className="fp-phase">
            <span className={`fp-phase-lbl ${phase === "before" ? "before" : phase === "during" ? "during" : "after"}`}>
              {phase === "before" ? "Before the Run" : phase === "during" ? "During the Run" : "After the Run"}
            </span>
            <span style={{ fontSize: 11, color: "var(--muted)" }}>
              {phase === "before"
                ? "Do these before you leave the house"
                : phase === "during"
                  ? "At the run. Keep it to 1 action."
                  : "Log before you close the app"}
            </span>
          </div>
          <div className="fp-items">
            {FIELD_TASKS[phase].map((t) => {
              const done = !!state.fieldChecks[t.id];
              return (
                <div key={t.id} className="fp-item">
                  <button
                    type="button"
                    className={`fp-check ${done ? "done" : ""}`}
                    onClick={() => toggleFieldCheck(t.id)}
                    aria-pressed={done}
                  />
                  <div>
                    <div className="fp-text" style={done ? { opacity: 0.5, textDecoration: "line-through" } : {}}>
                      {t.text}
                    </div>
                    <div className="fp-note">{t.note}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="ac-focus-box ac-topbar-full">
        <div className="ac-fb-eyebrow">The only question that matters</div>
        <div className="ac-fb-text ac-serif">
          Was RunCards checked before a run decision?{" "}
          <em>Everything below is designed to make that inevitable.</em>
        </div>
      </div>

      <div className="ac-section-label ac-topbar-full">
        Real-time KPIs{" "}
        <span style={{ fontSize: 9, fontWeight: 400, color: "var(--dim)", textTransform: "none", letterSpacing: 0 }}>
          — confirm signals, don&apos;t chase numbers
        </span>
      </div>
      <div className="ac-kpi-bar ac-topbar-full">
        <KpiCard
          cls="ac-kpi-mentions"
          label="Pre-run Mentions"
          val={kpis.mentions}
          target="Target: 6 organizers saying the line"
          fillPct={kpiFill("mentions", 6)}
          onOpen={() => onOpenKpi("mentions")}
        />
        <KpiCard
          cls="ac-kpi-clubs"
          label="Clubs Activated"
          val={activeClubKpi}
          target="Target: 3 anchor clubs (depth first)"
          fillPct={Math.min(100, (activeClubKpi / 3) * 100)}
          onOpen={() => onOpenKpi("clubs")}
        />
        <KpiCard
          cls="ac-kpi-sharing"
          label="Chat Intercepts"
          val={kpis.sharing}
          target={`Target: 5 "what's tomorrow?" replies`}
          fillPct={kpiFill("sharing", 5)}
          onOpen={() => onOpenKpi("sharing")}
        />
        <KpiCard
          cls="ac-kpi-travelers"
          label="Traveler Page Views"
          val={kpis.travelers}
          target="Target: 50 views/week from non-local IPs"
          fillPct={kpiFill("travelers", 50)}
          onOpen={() => onOpenKpi("travelers")}
        />
      </div>

      <div className="ac-section-label ac-topbar-full">2-Week Sprint — ★ tasks are non-negotiable</div>
      <div className="ac-week-grid ac-topbar-full">
        <WeekColumn week="week1" tasks={SPRINT_TASKS.week1} state={state} toggleTask={toggleTask} gllOpen={gllOpen} setGllOpen={setGllOpen} />
        <WeekColumn week="week2" tasks={SPRINT_TASKS.week2} state={state} toggleTask={toggleTask} gllOpen={gllOpen} setGllOpen={setGllOpen} />
      </div>

      <div className="ac-signal-log ac-topbar-full">
        <div className="ac-sl-title">Signal Log — center of gravity</div>
        <div className="ac-sl-sub">
          Log real-world evidence that RunCards was checked before a run. 3–5 per week minimum. Tag the
          source — this tells you which loop is actually working.
        </div>
        <div>
          {state.signals.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--dim)", padding: "10px 0" }}>
              No signals logged yet. The first real-world evidence RunCards was checked before a run is the
              most important thing to capture.
            </div>
          ) : (
            [...state.signals].reverse().map((s, i) => (
              <div key={`${i}-${s.date}`} className="ac-sl-entry">
                <div className={`ac-sl-dot signal-${s.type}`} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="ac-sl-text">{s.text}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                    <span className={`ac-sl-source src-${s.source || "other"}`}>
                      {SOURCE_LABELS[s.source] || "Other"}
                    </span>
                    <span style={{ fontSize: 10, color: "var(--dim)" }}>{s.date}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="ac-sl-add-row">
          <input
            className="ac-sl-input"
            value={signalText}
            onChange={(e) => setSignalText(e.target.value)}
            placeholder="e.g. Sarah from North Brooklyn Runners said the line before Thursday AM run..."
          />
          <select
            className="ac-sl-signal-sel"
            value={signalType}
            onChange={(e) => setSignalType(e.target.value)}
          >
            <option value="strong">Strong signal</option>
            <option value="mid">Mid signal</option>
            <option value="weak">Weak signal</option>
          </select>
          <select
            className="ac-sl-signal-sel"
            style={{ minWidth: 96 }}
            value={signalSource}
            onChange={(e) => setSignalSource(e.target.value)}
          >
            <option value="organizer">Organizer</option>
            <option value="qr">QR</option>
            <option value="chat">Chat</option>
            <option value="traveler">Traveler</option>
            <option value="other">Other</option>
          </select>
          <button type="button" className="ac-btn accent sm" onClick={handleAddSignalInline}>
            Log
          </button>
        </div>
      </div>

      <div className="ac-friction-log ac-topbar-full">
        <div className="ac-fl-title">Field Friction Log → Product Fixes</div>
        <div className="ac-fl-sub">
          After every run: log anything that felt slow, confusing, or broken. Quick note only — the pattern
          here drives what Chris fixes next.
        </div>
        <div className="ac-fl-entries">
          {state.frictions.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--dim)", padding: "6px 0" }}>No friction logged yet.</div>
          ) : (
            [...state.frictions].reverse().map((f, i) => (
              <div key={`${i}-${f.date}`} className="ac-fl-entry">
                <div style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>⚡</div>
                <div className="ac-fl-entry-text">{f.text}</div>
                <div className="ac-fl-entry-date">{f.date}</div>
              </div>
            ))
          )}
        </div>
        <div className="ac-sl-add-row">
          <input
            className="ac-sl-input"
            style={{ flex: 1 }}
            value={frictionText}
            onChange={(e) => setFrictionText(e.target.value)}
            placeholder='"page blank for 1s" · "2 taps to see runs" · "search bar confusing"'
          />
          <button
            type="button"
            className="ac-btn"
            style={{ background: "var(--red)", color: "white", border: "none", padding: "8px 14px", borderRadius: 7, fontSize: 11, fontWeight: 700 }}
            onClick={() => {
              const t = frictionText.trim();
              if (!t) return;
              addFriction(t);
              setFrictionText("");
            }}
          >
            Log it
          </button>
        </div>
      </div>

      <div className="ac-week-verdict ac-topbar-full">
        <div className="ac-wv-title">End of Week Verdict</div>
        <div className="ac-wv-question ac-serif">Did someone check RunCards before a run this week?</div>
        <div className="ac-wv-buttons">
          <button type="button" className="ac-wv-yes" onClick={() => setWeekVerdict("yes")}>
            YES → Continue
          </button>
          <button type="button" className="ac-wv-no" onClick={() => setWeekVerdict("no")}>
            NO → Go deeper
          </button>
        </div>
        {weekVerdict === "yes" && (
          <div className="ac-wv-result show good">
            ✓ Continue. Stay in the same 1–2 clubs. Your job is to deepen density until the phone-sharing
            moment starts happening without you.
          </div>
        )}
        {weekVerdict === "no" && (
          <div className="ac-wv-result show bad">
            → Go deeper in 1 club. Don&apos;t spread to new clubs. Attend one run this week. Your only goal:
            create 1 &quot;what&apos;s that?&quot; moment.
          </div>
        )}
      </div>

      <div className="ac-kill-list ac-topbar-full">
        <div className="ac-kl-title">Do Not Do — Anti-Drift Protection</div>
        <div className="ac-kl-items">
          {KILL_LIST_ITEMS.map((x) => (
            <span key={x} className="ac-kl-item">
              {x}
            </span>
          ))}
        </div>
      </div>

      <div className="ac-panel ac-topbar-full">
        <div className="ac-panel-head">
          <div>
            <div className="ac-panel-title">Activation Progress</div>
            <div className="ac-panel-sub">NYC Phase 1 — three core moves unlock everything</div>
          </div>
        </div>
        <div className="ac-panel-body">
          <ProgRow label="Organizer Handshakes" fill={prog.p1.w} n={prog.p1.label} color="var(--green)" />
          <ProgRow label="Pre-run Mentions" fill={prog.p2.w} n={prog.p2.label} color="var(--accent)" />
          <ProgRow label="QR Cards Placed" fill={prog.p3.w} n={prog.p3.label} color="var(--amber)" />
          <ProgRow label="Chat Intercepts Seeded" fill={prog.p4.w} n={prog.p4.label} color="#6B4F8A" />
          <ProgRow label="Traveler Page Live" fill={prog.p5.w} n={prog.p5.label} color="var(--accent)" />
          <div className="ac-inline-note">
            <strong>Depth before breadth.</strong> Pick 3 clubs. Seed deeply until people are opening
            RunCards during runs and others notice. The &quot;what&apos;s that?&quot; moment is the real growth
            engine.
          </div>
        </div>
      </div>
    </>
  );
}
